/**
 * Top-level routing. Signed-out users see the sign-in flow; a signed-in user who must
 * change their password or enrol MFA sees only that step. Every route in ROUTES is
 * guarded by its permissions (from auth/me); the API enforces them again.
 */
import { ShieldOff } from 'lucide-react';
import { lazy, Suspense } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { EmptyState, PageBody, Spinner } from '../components/ui';
import { BRAND } from '../config/brand';
import { useAuth } from '../lib/auth';
import { MfaSetup, SetPassword, SignIn } from './auth/SignIn';
import { AppShell } from './shell/AppShell';
import { DemoBanner } from './shell/DemoBanner';
import { EXTRA_ROUTES, ROUTES, type RouteDef } from './routes';

function Guard({ route }: { route: RouteDef }) {
  const { can } = useAuth();
  const Page = route.page;
  if (!can(...route.perm)) return <NoAccess />;
  return <Page />;
}

export function NoAccess() {
  return (
    <PageBody>
      <EmptyState
        icon={<ShieldOff aria-hidden />}
        title="This screen is not part of your role"
        body="Ask your organisation’s administrator if you need it. Your role and the cabinets shared with you decide what you can open."
        action={
          <Link to="/" className="inline-flex min-h-touch items-center rounded-tag bg-brand px-4 text-sm font-semibold text-brand-ink">
            Go to home
          </Link>
        }
      />
    </PageBody>
  );
}

function NotFound() {
  const { pathname } = useLocation();
  return (
    <PageBody>
      <EmptyState
        title="Page not found"
        body={
          <>
            Nothing lives at <span className="code">{pathname}</span>.
          </>
        }
        action={
          <Link to="/" className="inline-flex min-h-touch items-center rounded-tag bg-brand px-4 text-sm font-semibold text-brand-ink">
            Go to home
          </Link>
        }
      />
    </PageBody>
  );
}

// D4 public pages (no account): share-link recipient view and certified-copy check.
const SharePublicPage = lazy(() => import('./pages/public/SharePublicPage'));
const VerifyPage = lazy(() => import('./pages/public/VerifyPage'));
// e-Sign (ES2): public check of a signed + sealed document (QR on the certificate of completion).
const VerifySignPage = lazy(() => import('./pages/public/VerifySignPage'));
// NotaVera N7 (PRD §10 NV-VER): the public verification page. /verify/<NV-… | NVD-…> is NotaVera's; any other token stays
// QXM's records certified-copy check (/verify/:token) — the two never collide because NotaVera codes carry the NV prefix.
const NvVerifyPage = lazy(() => import('./pages/public/NvVerifyPage'));
// NotaVera N9: public status page (FR-OPS-04) and the training certificate check (FR-TRN-02).
const StatusPage = lazy(() => import('./pages/ops/StatusPage'));
const TrainingVerifyPage = lazy(() => import('./pages/public/TrainingVerifyPage'));
import { MaintenanceBanner } from './pages/ops/MaintenanceBanner';
import { isNotaVeraCode } from './pages/public/nv-verify';
function VerifyDispatch() {
  const { token } = useParams();
  return isNotaVeraCode(token) ? <NvVerifyPage /> : <VerifyPage />;
}
// NotaVera N2 (PRD §5): the party portal — invite link, then e-KYC without an account.
const InvitePage = lazy(() => import('./pages/party/InvitePage'));
const PartyPortalPage = lazy(() => import('./pages/party/PartyPortalPage'));

/** NotaVera N1 (PRD hard stop 15): screens outside the signed-in shell carry the demo banner too (the shell has its own). */
const Demo = ({ children }: { children: React.ReactNode }) => (
  <>
    <DemoBanner />
    <MaintenanceBanner />
    {children}
  </>
);

export function App() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (/^\/(s|verify|verify-sign|p)\//.test(location.pathname) || location.pathname === '/party' || location.pathname === '/verify' || location.pathname === '/status') {
    return (
      <Demo>
      <Suspense fallback={<Spinner className="min-h-dvh" />}>
        <Routes>
          <Route path="/s/:token" element={<SharePublicPage />} />
          <Route path="/verify" element={<NvVerifyPage />} />
          <Route path="/verify/:token" element={<VerifyDispatch />} />
          <Route path="/verify/training/:code" element={<TrainingVerifyPage />} />
          <Route path="/status" element={<StatusPage />} />
          <Route path="/verify-sign/:id" element={<VerifySignPage />} />
          <Route path="/p/:token" element={<InvitePage />} />
          <Route path="/party" element={<PartyPortalPage />} />
        </Routes>
      </Suspense>
      </Demo>
    );
  }
  if (loading) return <Spinner label={`Opening ${BRAND.product}…`} className="min-h-dvh" />;
  if (!user) return <Demo><SignIn /></Demo>;
  if (user.mustChangePassword) return <Demo><SetPassword /></Demo>;
  if (user.mfaRequired && !user.mfaEnabled) return <Demo><MfaSetup /></Demo>;
  return (
    <Suspense fallback={<Spinner className="min-h-dvh" />}>
      <Routes>
        <Route element={<AppShell />}>
          {ROUTES.map((r) => (
            <Route key={r.path} path={r.path} element={<Guard route={r} />} />
          ))}
          {EXTRA_ROUTES.map((r) => (
            <Route key={r.path} path={r.path} element={<r.page />} />
          ))}
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
